import { AlertCircle, CheckCircle, Clock, Mail, Phone, MapPin, Award, ChevronLeft, ChevronRight, DollarSign, FileText } from 'lucide-react'
import { useEffect, useMemo, useRef, useState } from 'react'
import { Partner, Quotation } from '../../types/maintenance'

type TransitionPhase = 'idle' | 'leaving' | 'pre-enter' | 'entering'

const EXIT_ANIMATION_DURATION = 140
const ENTER_ANIMATION_DELAY = 24
const ENTER_ANIMATION_DURATION = 220

interface PartnerInfoPanelProps {
  partner?: Partner | null
  partners?: Partner[]
  assetId?: string
  showWarning?: boolean
  compact?: boolean
  // Props para cotações (usado em compras)
  quotations?: Quotation[]
  selectedQuotationId?: string | null
  quotationsFulfilled?: boolean
  // Quando true, oculta o carrossel/dados do parceiro e mostra apenas a secção
  // de cotações (utilizado em qualquer fase do fluxo de compras, onde o
  // "carrossel de parceiros" não faz sentido).
  hidePartnerCarousel?: boolean
}

export default function PartnerInfoPanel({
  partner,
  partners,
  assetId,
  showWarning = true,
  compact = false,
  quotations = [],
  selectedQuotationId = null,
  quotationsFulfilled = false,
  hidePartnerCarousel = false
}: PartnerInfoPanelProps) {
  const availablePartners = useMemo<Partner[]>(() => {
    if (partners && partners.length > 0) return partners
    if (partner) return [partner]
    return []
  }, [partner, partners])

  const totalPartners = availablePartners.length
  const [currentPartnerIndex, setCurrentPartnerIndex] = useState(0)
  const [pendingPartnerIndex, setPendingPartnerIndex] = useState<number | null>(null)
  const [transitionDirection, setTransitionDirection] = useState<'next' | 'prev'>('next')
  const [transitionPhase, setTransitionPhase] = useState<TransitionPhase>('idle')

  const animationTimeoutRef = useRef<number | null>(null)

  useEffect(() => {
    return () => {
      if (animationTimeoutRef.current) {
        window.clearTimeout(animationTimeoutRef.current)
        animationTimeoutRef.current = null
      }
    }
  }, [])

  // Clamp current index to valid range (derived during render instead of setState in effect)
  const safeCurrentIndex = totalPartners === 0 ? 0 : (currentPartnerIndex >= totalPartners ? 0 : currentPartnerIndex)

  useEffect(() => {
    if (transitionPhase === 'idle') {
      return
    }

    if (animationTimeoutRef.current) {
      window.clearTimeout(animationTimeoutRef.current)
      animationTimeoutRef.current = null
    }

    if (transitionPhase === 'leaving') {
      animationTimeoutRef.current = window.setTimeout(() => {
        if (pendingPartnerIndex !== null) {
          setCurrentPartnerIndex(pendingPartnerIndex)
        }
        setTransitionPhase('pre-enter')
      }, EXIT_ANIMATION_DURATION)
    } else if (transitionPhase === 'pre-enter') {
      animationTimeoutRef.current = window.setTimeout(() => {
        setTransitionPhase('entering')
      }, ENTER_ANIMATION_DELAY)
    } else if (transitionPhase === 'entering') {
      animationTimeoutRef.current = window.setTimeout(() => {
        setTransitionPhase('idle')
        setPendingPartnerIndex(null)
      }, ENTER_ANIMATION_DURATION)
    }

    return () => {
      if (animationTimeoutRef.current) {
        window.clearTimeout(animationTimeoutRef.current)
        animationTimeoutRef.current = null
      }
    }
  }, [transitionPhase, pendingPartnerIndex])

  // Render "quotations-only": oculta o carrossel/dados do parceiro e mostra
  // apenas a secção de cotações. Usado em qualquer fase do fluxo de compras.
  // Esta verificação fica ANTES da early-return de `totalPartners === 0`
  // porque queremos que o modo "cotações-only" funcione mesmo quando o ativo
  // não tem parceiros atribuídos.
  const renderQuotationsList = () => {
    if (!quotations || quotations.length === 0) return null
    return (
      <div className="space-y-2">
        {quotations.map((quotation) => (
          <div
            key={quotation.id}
            className={`rounded-lg border p-3 ${
              selectedQuotationId === quotation.id
                ? 'bg-green-50 border-green-200 ring-2 ring-green-500'
                : 'bg-gray-50 border-gray-200'
            }`}
          >
            <div className="flex items-start justify-between gap-3">
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 mb-1">
                  <span className="font-semibold text-gray-900 text-sm">{quotation.partnerName}</span>
                  {selectedQuotationId === quotation.id && (
                    <CheckCircle className="h-4 w-4 text-green-600 flex-shrink-0" />
                  )}
                </div>
                <div className="flex items-center gap-2 text-sm">
                  <DollarSign className="h-3.5 w-3.5 text-green-600" />
                  <span className="font-semibold text-green-700">
                    €{Number(quotation.quotedValue).toFixed(2)}
                  </span>
                </div>
                {quotation.deliveryTime && (
                  <div className="mt-1 text-xs text-gray-600">
                    Prazo: {quotation.deliveryTime}
                  </div>
                )}
                {quotation.notes && (
                  <div className="mt-1 text-xs text-gray-500 italic">
                    {quotation.notes}
                  </div>
                )}
              </div>
              <div className="text-[10px] text-gray-500">
                {new Date(quotation.receivedAt).toLocaleDateString('pt-PT')}
              </div>
            </div>
          </div>
        ))}
      </div>
    )
  }

  const renderQuotationsSection = () => {
    if (!quotations || quotations.length === 0) return null
    return (
      <div className="border-t border-gray-100 pt-4">
        <div className="flex items-center justify-between mb-3">
          <h5 className="text-sm font-semibold text-gray-700 flex items-center gap-2">
            <FileText className="h-4 w-4 text-blue-600" />
            Cotações Recebidas
          </h5>
          <span className={`inline-flex items-center px-2.5 py-1 rounded-full text-xs font-semibold ${
            quotationsFulfilled
              ? 'bg-green-100 text-green-700 border border-green-200'
              : 'bg-yellow-100 text-yellow-700 border border-yellow-200'
          }`}>
            {quotations.length}/3 {quotationsFulfilled ? 'Completas' : 'Pendentes'}
          </span>
        </div>
        {renderQuotationsList()}
      </div>
    )
  }

  // Render "quotations-only": oculta o carrossel/dados do parceiro e mostra
  // apenas a secção de cotações. Usado em qualquer fase do fluxo de compras.
  if (hidePartnerCarousel) {
    if (!quotations || quotations.length === 0) return null
    return (
      <div className={`bg-white border border-gray-200 rounded-xl shadow-sm ${compact ? 'px-5 py-4' : 'px-6 pt-5 pb-6 sm:px-7'}`}>
        <div className={`flex items-center justify-between border-b border-gray-100 ${compact ? 'px-0 py-3' : 'px-6 py-4'}`}>
          <h3 className={`${compact ? 'text-sm' : 'text-lg'} font-semibold text-gray-900 flex items-center gap-2`}>
            <FileText className={`${compact ? 'h-4 w-4' : 'h-5 w-5'} text-blue-600`} />
            Cotações Recebidas
          </h3>
          <span className={`inline-flex items-center px-2.5 py-1 rounded-full text-xs font-semibold ${
            quotationsFulfilled
              ? 'bg-green-100 text-green-700 border border-green-200'
              : 'bg-yellow-100 text-yellow-700 border border-yellow-200'
          }`}>
            {quotations.length}/3 {quotationsFulfilled ? 'Completas' : 'Pendentes'}
          </span>
        </div>
        <div className={`${compact ? 'pt-3' : 'px-6 py-5'}`}>
          {renderQuotationsList()}
        </div>
      </div>
    )
  }

  if (totalPartners === 0) {
    return (
      <div className={`bg-gray-50 border border-gray-200 rounded-lg ${compact ? 'px-5 py-4' : 'px-6 py-5'}`}>
        <div className="flex items-center gap-2 text-gray-500">
          <AlertCircle className="h-5 w-5" />
          <span className="text-sm font-medium">Nenhum parceiro atribuído</span>
        </div>
        {showWarning && (
          <p className="mt-2 text-xs text-gray-600">
            Entre em contato com a equipa interna para atribuir um parceiro responsável.
          </p>
        )}
      </div>
    )
  }

  const currentPartner = availablePartners[safeCurrentIndex]
  const shouldShowNavigation = totalPartners > 1

  const isDefaultPartner = currentPartner.partnerId === 'PAR-0000'
  const isInactive = currentPartner.status !== 'ativo'
  const StatusIcon = isDefaultPartner || isInactive ? AlertCircle : CheckCircle
  const statusBadgeClasses = isDefaultPartner || isInactive
    ? 'text-amber-600 bg-amber-50 border-amber-200'
    : 'text-emerald-600 bg-emerald-50 border-emerald-200'

  const animationTransitionClasses =
    transitionPhase === 'pre-enter' ? 'transition-none' : 'transition-all duration-300 ease-out'

  const animationPhaseClasses = (() => {
    switch (transitionPhase) {
      case 'leaving':
        return transitionDirection === 'next'
          ? '-translate-x-5 opacity-0'
          : 'translate-x-5 opacity-0'
      case 'pre-enter':
        return transitionDirection === 'next'
          ? 'translate-x-6 opacity-0'
          : '-translate-x-6 opacity-0'
      case 'entering':
        return 'translate-x-0 opacity-100'
      default:
        return 'translate-x-0 opacity-100'
    }
  })()

  const animatedContainerClasses = `transform ${animationTransitionClasses} ${animationPhaseClasses}`

  const getSpecializations = () => {
    if (!currentPartner.specialization) return []
    return currentPartner.specialization.split(';').map(spec => spec.trim()).filter(Boolean)
  }

  const getRegions = () => {
    if (!currentPartner.regionsCovered) return []
    return currentPartner.regionsCovered.split(';').map(region => region.trim()).filter(Boolean)
  }

  const startTransition = (direction: 'next' | 'prev', targetIndex: number) => {
    if (!shouldShowNavigation) return
    if (transitionPhase !== 'idle') return

    setTransitionDirection(direction)
    setPendingPartnerIndex(targetIndex)
    setTransitionPhase('leaving')
  }

  const handlePreviousPartner = () => {
    if (!shouldShowNavigation) return

    const targetIndex = safeCurrentIndex === 0 ? totalPartners - 1 : safeCurrentIndex - 1
    startTransition('prev', targetIndex)
  }

  const handleNextPartner = () => {
    if (!shouldShowNavigation) return

    const targetIndex = safeCurrentIndex === totalPartners - 1 ? 0 : safeCurrentIndex + 1
    startTransition('next', targetIndex)
  }

  const handleSelectPartner = (index: number) => {
    if (!shouldShowNavigation) return
    if (index === safeCurrentIndex) return

    const direction = index > safeCurrentIndex ? 'next' : 'prev'
    startTransition(direction, index)
  }

  if (compact) {
    return (
      <div className="relative bg-gray-50 border border-gray-200 rounded-lg px-6 py-4">
        {shouldShowNavigation && (
          <>
            <button
              type="button"
              onClick={handlePreviousPartner}
              className="absolute -left-3 top-1/2 -translate-y-1/2 bg-white border border-gray-300 rounded-full p-2 shadow-sm hover:bg-gray-50 hover:shadow-md transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-blue-500"
              aria-label="Parceiro anterior"
            >
              <ChevronLeft className="h-3.5 w-3.5 text-gray-600" />
            </button>
            <button
              type="button"
              onClick={handleNextPartner}
              className="absolute -right-3 top-1/2 -translate-y-1/2 bg-white border border-gray-300 rounded-full p-2 shadow-sm hover:bg-gray-50 hover:shadow-md transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-blue-500"
              aria-label="Próximo parceiro"
            >
              <ChevronRight className="h-3.5 w-3.5 text-gray-600" />
            </button>
          </>
        )}

        <div className="overflow-hidden">
          <div
            className={`${animatedContainerClasses} space-y-3`}
            data-phase={transitionPhase}
            data-testid="partner-card"
          >
            <div className="flex items-start justify-between gap-3">
              <div className="flex items-center gap-2">
                <StatusIcon className={`h-4 w-4 ${isDefaultPartner || isInactive ? 'text-amber-500' : 'text-emerald-500'}`} />
                <span className="font-semibold text-gray-900 text-sm leading-tight">{currentPartner.name}</span>
              </div>
              <div className="flex items-center gap-2">
                {shouldShowNavigation && (
                  <span className="text-[11px] uppercase tracking-wide text-gray-500">
                    {safeCurrentIndex + 1}/{totalPartners}
                  </span>
                )}
                <div className={`inline-flex items-center gap-1 px-2 py-1 rounded-full text-[10px] font-semibold border ${statusBadgeClasses}`}>
                  {isDefaultPartner ? 'Padrão' : currentPartner.status}
                </div>
              </div>
            </div>

            <div className="space-y-1.5 text-xs text-gray-600">
              <div className="flex items-center gap-2">
                <Mail className="h-3 w-3 text-gray-400" />
                <span>{currentPartner.primaryEmail}</span>
              </div>
              <div className="flex items-center gap-2">
                <Phone className="h-3 w-3 text-gray-400" />
                <span>{currentPartner.primaryPhone}</span>
              </div>
            </div>

            {showWarning && (isDefaultPartner || isInactive) && (
              <div className="rounded-md border border-amber-200 bg-amber-50 px-3 py-2 text-[11px] text-amber-700">
                {isDefaultPartner
                  ? 'Parceiro padrão - Contacte equipe interna para confirmar responsável.'
                  : 'Parceiro inativo - Verifique disponibilidade antes de prosseguir.'}
              </div>
            )}
          </div>
        </div>

        {shouldShowNavigation && (
          <div className="mt-3 flex items-center justify-center gap-1.5">
            {availablePartners.map((_, index) => (
              <button
                key={index}
                type="button"
                onClick={() => handleSelectPartner(index)}
                className={`h-1.5 rounded-full transition-all duration-300 focus:outline-none focus:ring-1 focus:ring-blue-400 ${
                  index === safeCurrentIndex ? 'w-5 bg-blue-500' : 'w-2 bg-gray-300 hover:bg-gray-400'
                }`}
                aria-label={`Ir para parceiro ${index + 1}`}
              />
            ))}
          </div>
        )}
      </div>
    )
  }

  return (
    <div className="relative bg-white border border-gray-200 rounded-xl shadow-sm px-6 pt-5 pb-6 sm:px-7">
      {shouldShowNavigation && (
        <>
          <button
            type="button"
            onClick={handlePreviousPartner}
            className="absolute -left-4 top-1/2 -translate-y-1/2 bg-white border border-gray-300 rounded-full p-3 shadow-md hover:shadow-lg hover:bg-gray-50 transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-blue-500"
            aria-label="Parceiro anterior"
          >
            <ChevronLeft className="h-4 w-4 text-gray-600" />
          </button>
          <button
            type="button"
            onClick={handleNextPartner}
            className="absolute -right-4 top-1/2 -translate-y-1/2 bg-white border border-gray-300 rounded-full p-3 shadow-md hover:shadow-lg hover:bg-gray-50 transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-blue-500"
            aria-label="Próximo parceiro"
          >
            <ChevronRight className="h-4 w-4 text-gray-600" />
          </button>
        </>
      )}

      <div className="overflow-hidden rounded-lg border border-gray-100">
        <div
          className={`${animatedContainerClasses} bg-white`}
          data-phase={transitionPhase}
          data-testid="partner-card"
        >
          <div className="flex items-center justify-between border-b border-gray-100 px-6 py-4">
            <div className="flex items-center gap-2">
              <StatusIcon className={`h-5 w-5 ${isDefaultPartner || isInactive ? 'text-amber-500' : 'text-emerald-500'}`} />
              <h3 className="text-lg font-semibold text-gray-900 leading-tight">
                Parceiro Responsável
                {shouldShowNavigation && (
                  <span className="ml-3 text-sm font-medium text-gray-600">
                    ({safeCurrentIndex + 1} de {totalPartners})
                  </span>
                )}
              </h3>
            </div>
            <div className={`inline-flex items-center gap-1 px-2.5 py-1.5 rounded-full text-xs font-semibold border ${statusBadgeClasses}`}>
              {isDefaultPartner ? 'Padrão' : currentPartner.status}
            </div>
          </div>

          <div className="px-6 py-5 space-y-5">
            <div>
              <h4 className="text-lg font-semibold text-gray-900">{currentPartner.name}</h4>
              <div className="mt-2 space-y-1.5 text-sm text-gray-600">
                <div className="flex items-center gap-3">
                  <Mail className="h-4 w-4 text-gray-400" />
                  <span>{currentPartner.primaryEmail}</span>
                </div>
                <div className="flex items-center gap-3">
                  <Phone className="h-4 w-4 text-gray-400" />
                  <span>{currentPartner.primaryPhone}</span>
                </div>
                {currentPartner.secondaryEmail && (
                  <div className="flex items-center gap-3 text-xs">
                    <Mail className="h-3.5 w-3.5 text-gray-400" />
                    <span>Sec: {currentPartner.secondaryEmail}</span>
                  </div>
                )}
                {currentPartner.secondaryPhone && (
                  <div className="flex items-center gap-3 text-xs">
                    <Phone className="h-3.5 w-3.5 text-gray-400" />
                    <span>Sec: {currentPartner.secondaryPhone}</span>
                  </div>
                )}
              </div>
            </div>

            {getSpecializations().length > 0 && (
              <div>
                <h5 className="text-sm font-semibold text-gray-700 mb-2">Especializações</h5>
                <div className="flex flex-wrap gap-2">
                  {getSpecializations().map((spec, index) => (
                    <span
                      key={`${spec}-${index}`}
                      className="inline-flex items-center gap-1 rounded-full bg-blue-50 px-3 py-1 text-xs font-semibold text-blue-700"
                    >
                      <Award className="h-3.5 w-3.5" />
                      {spec}
                    </span>
                  ))}
                </div>
              </div>
            )}

            {getRegions().length > 0 && (
              <div>
                <h5 className="text-sm font-semibold text-gray-700 mb-2">Regiões atendidas</h5>
                <div className="flex flex-wrap gap-2">
                  {getRegions().map((region, index) => (
                    <span
                      key={`${region}-${index}`}
                      className="inline-flex items-center gap-1 rounded-full bg-gray-100 px-3 py-1 text-xs font-semibold text-gray-700"
                    >
                      <MapPin className="h-3.5 w-3.5" />
                      {region}
                    </span>
                  ))}
                </div>
              </div>
            )}

            {currentPartner.slaHours && (
              <div className="flex items-center gap-3 text-sm text-gray-600">
                <Clock className="h-4 w-4 text-gray-400" />
                <span>
                  SLA: <span className="font-semibold">{currentPartner.slaHours} horas</span>
                </span>
              </div>
            )}

            {/* Seção de Cotações (para compras) */}
            {renderQuotationsSection()}

            {currentPartner.notes && (
              <div>
                <h5 className="text-sm font-semibold text-gray-700 mb-2">Observações</h5>
                <p className="text-sm text-gray-600 leading-relaxed">{currentPartner.notes}</p>
              </div>
            )}

            {showWarning && (isDefaultPartner || isInactive) && (
              <div className={`rounded-lg border px-4 py-3 ${
                isDefaultPartner ? 'bg-amber-50 border-amber-200' : 'bg-red-50 border-red-200'
              }`}>
                <div className="flex items-start gap-3">
                  <AlertCircle className={`h-5 w-5 mt-0.5 ${isDefaultPartner ? 'text-amber-600' : 'text-red-600'}`} />
                  <div className="text-sm">
                    <p className={`font-semibold ${isDefaultPartner ? 'text-amber-800' : 'text-red-800'}`}>
                      {isDefaultPartner ? 'Parceiro padrão' : 'Parceiro inativo'}
                    </p>
                    <p className={`mt-1 leading-relaxed ${isDefaultPartner ? 'text-amber-700' : 'text-red-700'}`}>
                      {isDefaultPartner
                        ? 'Este é o parceiro padrão. Entre em contato com a equipe interna para confirmar o responsável adequado.'
                        : 'Este parceiro está atualmente inativo. Verifique a disponibilidade antes de prosseguir com a solicitação.'}
                    </p>
                  </div>
                </div>
              </div>
            )}

            {assetId && (
              <div className="border-t border-gray-100 pt-3 text-xs text-gray-500">
                Referência do equipamento: <span className="font-mono text-gray-700">{assetId}</span>
              </div>
            )}
          </div>
        </div>
      </div>

      {shouldShowNavigation && (
        <div className="mt-4 flex items-center justify-center gap-2">
          {availablePartners.map((_, index) => (
            <button
              key={index}
              type="button"
              onClick={() => handleSelectPartner(index)}
              className={`h-2 rounded-full transition-all duration-300 focus:outline-none focus:ring-2 focus:ring-blue-300 ${
                index === safeCurrentIndex ? 'w-6 bg-blue-500' : 'w-2.5 bg-gray-300 hover:bg-gray-400'
              }`}
              aria-label={`Ir para parceiro ${index + 1}`}
            />
          ))}
        </div>
      )}
    </div>
  )
}